import { useState, useEffect } from 'react'

interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISSED_KEY = 'mackinac-install-prompt-dismissed'

export function useInstallPrompt() {
    const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null)
    const [isInstalled, setIsInstalled] = useState(false)

    useEffect(() => {
        if (window.matchMedia('(display-mode: standalone)').matches) {
            setIsInstalled(true)
            return
        }

        if (localStorage.getItem(DISMISSED_KEY)) return

        function handleBeforeInstallPrompt(e: Event) {
            e.preventDefault()
            setPromptEvent(e as BeforeInstallPromptEvent)
        }

        function handleAppInstalled() {
            setIsInstalled(true)
            setPromptEvent(null)
        }

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
        window.addEventListener('appinstalled', handleAppInstalled)

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
            window.removeEventListener('appinstalled', handleAppInstalled)
        }
    }, [])

    async function install() {
        if (!promptEvent) return false
        try {
            await promptEvent.prompt()
            const { outcome } = await promptEvent.userChoice
            setPromptEvent(null)
            return outcome === 'accepted'
        } catch {
            setPromptEvent(null)
            return false
        }
    }

    function dismiss() {
        localStorage.setItem(DISMISSED_KEY, 'true')
        setPromptEvent(null)
    }

    return {
        canInstall: !!promptEvent && !isInstalled,
        isInstalled,
        install,
        dismiss,
    }
}
